import axios from 'axios';

const API_BASE_URL = 'https://apiservices.bookingwhizz.com/api';

/**
 * WhatsApp OTP API Service
 * Handles WhatsApp OTP sending and verification
 */

/**
 * Send WhatsApp OTP to a phone number
 * @param {Object} params - Request parameters
 * @param {string} params.phoneNumber - Mobile number without country code, e.g. "3111094114"
 * @param {string} params.mobileCode - Country code with + prefix, e.g. "+92"
 * @param {string} params.partnerKey - Partner API Key from accommodationGroupDetail
 * @returns {Promise<Object>} Response from WhatsApp OTP API
 */
export const sendWhatsAppOtp = async (params) => {
  try {
    const { phoneNumber, mobileCode, partnerKey } = params;

    if (!phoneNumber) {
      throw new Error('Phone number is required');
    }

    if (!partnerKey) {
      throw new Error('Partner API Key is required');
    }

    const formData = new FormData();
    formData.append('phoneNumber', phoneNumber);
    if (mobileCode) {
      formData.append('mobileCode', mobileCode);
    }

    const response = await axios.post(
      `${API_BASE_URL}/member/code/wp-send`,
      formData,
      {
        headers: {
          'Partner-Api-Key': partnerKey,
          'Accept': 'application/json'
        },
        timeout: 30000
      }
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to send OTP');
    } else if (error.request) {
      throw new Error('Network error: Please check your internet connection');
    } else {
      throw new Error(error.message || 'An error occurred while sending OTP');
    }
  }
};

/**
 * Verify WhatsApp OTP code
 * @param {Object} params - Request parameters
 * @param {string} params.phoneNumber - Mobile number without country code, e.g. "3111094114"
 * @param {string} params.code - OTP code entered by user
 * @param {string} params.mobileCode - Country code with + prefix, e.g. "+92"
 * @param {string} params.partnerKey - Partner API Key from accommodationGroupDetail
 * @returns {Promise<Object>} Response from WhatsApp OTP verify API
 */
export const verifyWhatsAppOtp = async (params) => {
  try {
    const { phoneNumber, code, mobileCode, partnerKey } = params;

    if (!phoneNumber) {
      throw new Error('Phone number is required');
    }

    if (!code) {
      throw new Error('OTP code is required');
    }

    if (!partnerKey) {
      throw new Error('Partner API Key is required');
    }

    const formData = new FormData();
    formData.append('phoneNumber', phoneNumber);
    formData.append('code', code);
    formData.append('mobileCode', mobileCode || '');
    formData.append('autoRegister', '1');
    formData.append('quickSignUp', '1');
    formData.append('sourceId', '56');

    const response = await axios.post(
      `${API_BASE_URL}/member/code/wp-check`,
      formData,
      {
        headers: {
          'Partner-Api-Key': partnerKey,
          'Accept': 'application/json'
        },
        timeout: 30000
      }
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'OTP verification failed');
    } else if (error.request) {
      throw new Error('Network error: Please check your internet connection');
    } else {
      throw new Error(error.message || 'An error occurred during OTP verification');
    }
  }
};
